import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography, Chip, MenuItem } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import {
  fetchLgAccount,
  lgConnect,
  lgDisconnectAccount,
  lgPurgeAccount,
  lgSync,
  fetchLgAdminSummary,
  fetchLgDevices,
  LgDevice,
} from '../../services/lgApi';
import { eventBus } from '../../services/EventBus';
import LgSetupHelpDialog from './LgSetupHelpDialog';
import {
  DiscoveryCell,
  DiscoveryRegisteredTable,
  discoveryRegisteredShellSx,
  discoveryRegisteredCol as col,
  discoveryRegisteredRowSx,
  useDiscoveryColumnSort,
} from '../shared/DiscoverySortableHeader';

const ACCENT = '#2391FF';
const COUNTRY_OPTIONS = ['AR', 'US', 'BR', 'MX', 'ES', 'CL', 'CO', 'UY'];

function formatLgDeviceType(deviceType: string): string {
  return String(deviceType || '')
    .replace(/^DEVICE_/i, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || '—';
}

type LgSortKey = 'name' | 'type' | 'ip' | 'model' | 'power' | 'status' | 'via';

interface Props {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onGoToDevices?: () => void;
}

const LgDiscoveryTab: React.FC<Props> = ({ isDemoUser, onDemoActionAttempt, onGoToDevices }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [pat, setPat] = useState('');
  const [countryCode, setCountryCode] = useState('AR');
  const [syncResult, setSyncResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [unassigned, setUnassigned] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [devices, setDevices] = useState<LgDevice[]>([]);

  const refresh = useCallback(async () => {
    try {
      const [acc, sum, list] = await Promise.all([
        fetchLgAccount().catch(() => null),
        fetchLgAdminSummary().catch(() => ({ total: 0, unassigned_count: 0 })),
        fetchLgDevices().catch(() => [] as LgDevice[]),
      ]);
      setAccount(acc);
      setUnassigned(sum.unassigned_count || 0);
      setDevices(Array.isArray(list) ? list : []);
      if (acc?.country_code) setCountryCode(acc.country_code);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const handleConnect = async () => {
    if (blockDemo()) return;
    if (!pat.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const acc = await lgConnect(pat.trim(), countryCode);
      setAccount(acc);
      setPat('');
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('settings.lgDiscovery.errorConnect', { defaultValue: 'PAT inválido o error de conexión' }));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.lgDiscovery.disconnectConfirm', { defaultValue: '¿Desconectar cuenta ThinQ?' }))) return;
    setBusy(true);
    try {
      await lgDisconnectAccount();
      setAccount(null);
      setSyncResult(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handlePurge = async () => {
    if (blockDemo()) return;
    if (!window.confirm(t('settings.lgDiscovery.purgeConfirm', {
      defaultValue: 'Se borran la cuenta y las claves. Para volver a conectar hay que cargarlas de nuevo. Las tarjetas de dispositivos no se borran.',
    }))) return;
    setBusy(true);
    try {
      await lgPurgeAccount();
      setAccount(null);
      setSyncResult(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    if (blockDemo()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await lgSync();
      setSyncResult(res);
      eventBus.emit('estructura:refrescar');
      await refresh();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || 'Error sync'));
    } finally {
      setBusy(false);
    }
  };

  const formatLastRun = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    return `${d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
  };

  const powerLabel = (power?: boolean | null) => {
    if (power == null) return { text: '—', color: '#888' };
    return power
      ? { text: t('settings.discoveryCols.powerOn'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.powerOff'), color: '#999' };
  };

  const statusLabel = (d: LgDevice) => {
    if (d.missing_from_cloud) {
      return { text: t('settings.discoveryCols.statusMissing'), color: '#e5a54b' };
    }
    return d.online
      ? { text: t('settings.discoveryCols.statusOnline'), color: '#66bb6a' }
      : { text: t('settings.discoveryCols.statusOffline'), color: '#999' };
  };

  const typeLabel = (d: LgDevice) => (
    d.ui_family === 'ac' ? t('settings.discoveryCols.kindAc') : formatLgDeviceType(d.device_type)
  );

  const getSortValue = useCallback((d: LgDevice, key: LgSortKey) => {
    if (key === 'name') return d.nombre || '';
    if (key === 'type') return typeLabel(d);
    if (key === 'ip') return '';
    if (key === 'model') return d.modelo || '';
    if (key === 'power') return powerLabel(d.power).text;
    if (key === 'via') return t('settings.discoveryCols.viaLg');
    return statusLabel(d).text;
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- labels follow t

  const { sorted: sortedDevices, sortKey, sortDir, toggleSort } = useDiscoveryColumnSort(devices, getSortValue);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress sx={{ color: ACCENT }} /></Box>;
  }

  const connected = Boolean(account?.connected);
  const lastRun = formatLastRun(account?.last_sync?.finished_at || account?.last_sync?.started_at);

  return (
    <Box sx={{ color: 'white', px: 0.5, pb: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 1,
      }}
      >
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: ACCENT, flex: 1, minWidth: 0 }}>
          {t('settings.lgDiscovery.accountTitle', { defaultValue: 'Cuenta del hogar (ThinQ Connect)' })}
        </Typography>
        <Chip
          icon={<HelpOutline sx={{ fontSize: '14px !important' }} />}
          label={t('settings.lgDiscovery.help.guideLabel', { defaultValue: 'Guía' })}
          size="small"
          clickable
          onClick={() => setHelpOpen(true)}
          sx={{
            height: 24,
            fontSize: 11,
            fontWeight: 700,
            color: ACCENT,
            bgcolor: 'rgba(35,145,255,0.1)',
            border: '1px solid rgba(35,145,255,0.45)',
            flexShrink: 0,
            '& .MuiChip-icon': { color: ACCENT, ml: 0.5 },
            '&:hover': { bgcolor: 'rgba(35,145,255,0.18)' },
          }}
        />
      </Box>

      {connected ? (
        <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 1.5, mb: 1.5, bgcolor: '#2F3235' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{account.display_name || 'ThinQ Connect'}</Typography>
            <Chip size="small" label={t('settings.lgDiscovery.connected', { defaultValue: 'Conectada' })} sx={{ bgcolor: 'rgba(102,187,106,0.2)', color: '#66bb6a' }} />
          </Box>
          <Typography sx={{ fontSize: 12, color: '#aaa' }}>
            {t('settings.lgDiscovery.accountMeta', {
              defaultValue: 'país: {{country}} · client_id: {{client}}',
              country: account.country_code || countryCode,
              client: account.client_id_masked || '…',
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={handleDisconnect} disabled={busy} sx={{ color: '#e57373', borderColor: '#e57373' }}>
              {t('settings.lgDiscovery.disconnect', { defaultValue: 'Desconectar' })}
            </Button>
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.lgDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {account?.credentials_stored && (
            <Button size="small" variant="contained" onClick={handlePurge} disabled={busy} sx={{ bgcolor: '#c62828', mb: 1.5, '&:hover': { bgcolor: '#b71c1c' } }}>
              {t('settings.lgDiscovery.purge', { defaultValue: 'Borrar' })}
            </Button>
          )}
          <Typography sx={{ fontSize: 12, color: '#999', mb: 0.5 }}>
            {t('settings.lgDiscovery.patHint', { defaultValue: 'Ingresá el PAT de ThinQ Connect y el país del hogar:' })}
          </Typography>
          <TextField
            fullWidth size="small" value={pat} onChange={(e) => setPat(e.target.value)}
            placeholder="PAT ThinQ Connect"
            sx={{ mb: 1, input: { color: 'white' }, '& .MuiOutlinedInput-root': { bgcolor: '#1a1a1a' } }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label={t('settings.lgDiscovery.countryLabel', { defaultValue: 'País' })}
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            sx={{ mb: 1, input: { color: 'white' }, '& .MuiOutlinedInput-root': { bgcolor: '#1a1a1a' }, '& .MuiInputLabel-root': { color: '#999' } }}
          >
            {COUNTRY_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <Button fullWidth variant="contained" onClick={handleConnect} disabled={busy || !pat.trim()} sx={{ bgcolor: ACCENT }}>
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.lgDiscovery.connect', { defaultValue: 'Conectar' })}
          </Button>
        </Box>
      )}

      {connected && (
        <>
          <Box sx={{ mb: 1.5 }}>
            {lastRun && (
              <Typography sx={{ fontSize: '0.7rem', color: '#888', mb: 1, textAlign: 'right' }}>
                {t('settings.lgDiscovery.lastRun', { defaultValue: 'Último' })}: {lastRun}
              </Typography>
            )}
            <Button fullWidth variant="contained" onClick={handleSync} disabled={busy} sx={{ bgcolor: ACCENT }}>
              {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.lgDiscovery.sync', { defaultValue: 'Sincronizar dispositivos' })}
            </Button>
          </Box>
          {syncResult && (
            <Typography sx={{ fontSize: 12, color: '#bbb', mb: 1.5 }}>
              +{syncResult.added} · ↻{syncResult.updated} · —{syncResult.already_known ?? 0} · ausentes {syncResult.missing}
              {syncResult.errors?.length ? ` · errores ${syncResult.errors.length}` : ''}
            </Typography>
          )}
        </>
      )}

      {error && <Typography sx={{ color: '#e57373', fontSize: 12, mb: 1.5 }}>{String(error)}</Typography>}

      <Box sx={discoveryRegisteredShellSx()}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
          {t('settings.lgDiscovery.devicesTitle', { defaultValue: 'Dispositivos registrados' })}
        </Typography>
        {devices.length === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
            {t('settings.lgDiscovery.emptyDevices', { defaultValue: 'Todavía no hay dispositivos sincronizados. Conectá la cuenta y sincronizá.' })}
          </Typography>
        ) : (
          <>
            <DiscoveryRegisteredTable
              columns={[
                { key: 'name', label: t('settings.discoveryCols.name'), sx: col.name },
                { key: 'type', label: t('settings.discoveryCols.type'), sx: col.type },
                { key: 'ip', label: t('settings.discoveryCols.ip'), sx: col.ip },
                { key: 'model', label: t('settings.discoveryCols.model'), sx: col.model },
                { key: 'power', label: t('settings.discoveryCols.power'), sx: col.power },
                { key: 'status', label: t('settings.discoveryCols.status'), sx: col.status },
                { key: 'via', label: t('settings.discoveryCols.via'), sx: col.via },
              ]}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggle={toggleSort}
            >
            {sortedDevices.map((d) => {
              const pl = powerLabel(d.power);
              const sl = statusLabel(d);
              const tipo = typeLabel(d);
              return (
                <Box key={d.id} sx={discoveryRegisteredRowSx}>
                  <DiscoveryCell label={t('settings.discoveryCols.name')} sx={col.name} title={d.nombre}>{d.nombre}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.type')} sx={col.type} title={tipo}>{tipo}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.ip')} sx={col.ip}>—</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.model')} sx={col.model} title={d.modelo || undefined}>{d.modelo || '—'}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.power')} sx={{ ...col.power, color: pl.color }}>{pl.text}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.status')} sx={{ ...col.status, color: sl.color }}>{sl.text}</DiscoveryCell>
                  <DiscoveryCell label={t('settings.discoveryCols.via')} sx={col.via}>{t('settings.discoveryCols.viaLg')}</DiscoveryCell>
                </Box>
              );
            })}
            </DiscoveryRegisteredTable>
          </>
        )}
      </Box>

      {unassigned > 0 && onGoToDevices && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={t('settings.lgDiscovery.goToDevices', { defaultValue: 'Ir a Dispositivos ({{count}} sin asignar)', count: unassigned })}
            onClick={onGoToDevices}
            clickable
            sx={{ backgroundColor: '#262626', color: '#fff' }}
          />
        </Box>
      )}

      <LgSetupHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default LgDiscoveryTab;
